import React, { type ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import ErrorMessage from 'atom-components/error-message-from-hook-form';
import Checkbox from 'atom-components/form-fields-for-hook-form/checkbox';
import FormLabelAndInputContainer from 'atom-components/form-label-and-input-container';
import Label from 'atom-components/label';
import TooltipItem from 'atom-components/tooltip-item';
import ActiveMarkerBadge from 'pages/marking/components/active-marker-badge';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import type { GroupMemberFormValue } from './marking-group-form.schema';
import { GroupMemberType } from './marking-group-form.schema';

type SharedProps = {
  groupMemberType: GroupMemberType;
  organisationUsers: BaseCustomerUser[];
};

type MarkerBadgeWithTooltipProps = {
  groupMember: GroupMemberFormValue;
} & SharedProps;

function MarkerBadgeWithTooltip({
  groupMember,
  groupMemberType,
  organisationUsers
}: MarkerBadgeWithTooltipProps): ReactElement {
  const organisationUser = organisationUsers.find((ou) => ou.id === groupMember.organisationUserId);

  const tooltip =
    `@${organisationUser?.user.username}` +
    `${
      groupMemberType === GroupMemberType.NonMarker
        ? ' has non-marker roles only that are not supported by marking groups.'
        : ''
    }`;

  return (
    <TooltipItem tooltip={tooltip}>
      <ActiveMarkerBadge
        organisationUser={organisationUser}
        // Display an orange warning if the user is in the non-marker list. This is
        // because non-markers aren't normally marking group members.
        displayWarning={groupMemberType === GroupMemberType.NonMarker}
        // Badge is not used in the context of a marking task, so ignore mark or
        // response specific props:
        defaultActiveMarkerToOrgUser
        activeMarker={undefined}
        isAiMarked={false}
      />
    </TooltipItem>
  );
}

const partialFieldName = 'GroupMembers';
const partialFieldLabel = 'Group Members';

export default function GroupMembers({
  groupMemberType,
  organisationUsers
}: SharedProps): ReactElement {
  const { watch, control, formState } = useFormContext();

  const fieldName = `${groupMemberType}${partialFieldName}`;
  const fieldLabel = `${partialFieldLabel} (${groupMemberType === 'nonMarker' ? 'Non-' : ''}Markers)`;

  // Loads a different groupMembers field based on groupMemberType:
  const groupMembers: GroupMemberFormValue[] = watch(fieldName);

  return (
    <FormLabelAndInputContainer>
      <Label htmlFor={fieldName}>{fieldLabel}</Label>
      {groupMembers.length > 0 && (
        <ul className="list-unstyled">
          {groupMembers.map((groupMember, index) => (
            <li key={groupMember.organisationUserId}>
              <Checkbox role="checkbox">
                <Checkbox.Input
                  id={`organisationUser-${groupMember.organisationUserId}`}
                  control={control}
                  name={`${fieldName}.${index}.checked`}
                  role="checkbox"
                />
                <Checkbox.Label htmlFor={`organisationUser-${groupMember.organisationUserId}`}>
                  <MarkerBadgeWithTooltip
                    groupMember={groupMember}
                    groupMemberType={groupMemberType}
                    organisationUsers={organisationUsers}
                  />
                </Checkbox.Label>
              </Checkbox>
            </li>
          ))}
        </ul>
      )}
      {formState.errors.groupMembers && (
        <ErrorMessage errors={formState.errors} name={`${fieldName}.root`} />
      )}
    </FormLabelAndInputContainer>
  );
}
